import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, Loader2, PackageCheck, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils/cn';
import { formatoCantidad, formatoFijo } from '@/lib/utils/formato-precio';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { comerciaService } from '../../services/comercia-service';
import type {
  DatosDeProducto,
  PartidaDeProducto,
  ProductoDeComercia,
  ProductoDelCatalogo,
  UnidadDeProducto,
} from '../../types/comercia.types';
import {
  COLORES_DE_CARGA,
  NOMBRE_DE_COLOR,
  UNIDADES,
  kg,
  m3,
  siglaDeUnidad,
  usd,
  volumenDe,
} from '../../utils/formatos';
import { BuscadorDePartida } from './buscador-de-partida';
import { CajaAEscala } from './caja-a-escala';

/** Lo que se precarga al abrir: un producto a editar, o lo que trajo el asistente. */
export interface ProductoInicial {
  producto?: ProductoDeComercia;
  descripcion?: string;
  partida?: PartidaDeProducto | null;
}

interface Formulario {
  descripcion: string;
  proveedor: string;
  partida: PartidaDeProducto | null;
  cantidad: string;
  unidad: UnidadDeProducto;
  largo: string;
  ancho: string;
  alto: string;
  peso: string;
  fob: string;
  color: string;
  fragil: boolean;
  materialId: string | null;
}

const numero = (s: string) => {
  const n = Number(s.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};
const texto = (n: number | null | undefined) => (n == null ? '' : String(n).replace('.', ','));

function inicial(i: ProductoInicial | null, color: string): Formulario {
  const p = i?.producto;
  return {
    descripcion: p?.descripcion ?? i?.descripcion ?? '',
    proveedor: p?.proveedor ?? '',
    partida: p?.partida ?? i?.partida ?? null,
    cantidad: p ? String(p.cantidad) : '',
    unidad: (p?.unidad as UnidadDeProducto) ?? 'UND',
    largo: texto(p?.largoCm),
    ancho: texto(p?.anchoCm),
    alto: texto(p?.altoCm),
    peso: texto(p?.pesoUnitario),
    fob: p ? texto(p.fobUnitario) : '',
    color: p?.color ?? color,
    fragil: p?.fragil ?? false,
    materialId: p?.materialId ?? null,
  };
}

/**
 * «Agregar producto» (maqueta de Melanie): qué se trae, de quién, con qué
 * partida, cuántos, cuánto mide y pesa cada bulto, cuánto cuesta en origen y con
 * qué color se va a ver en el plano de carga.
 *
 * Todos los campos son obligatorios, como en la maqueta: sin medidas no se puede
 * acomodar en el contenedor ni repartir el flete por volumen, y sin partida no
 * hay arancel.
 */
export function DialogoDeProducto({
  abierto,
  inicial: datosIniciales,
  colorSugerido,
  onCerrar,
  onGuardar,
}: {
  abierto: boolean;
  inicial: ProductoInicial | null;
  colorSugerido: string;
  onCerrar: () => void;
  onGuardar: (datos: DatosDeProducto, productoId?: string) => Promise<void>;
}) {
  const [f, setF] = useState<Formulario>(() => inicial(datosIniciales, colorSugerido));
  const [intentado, setIntentado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editando = !!datosIniciales?.producto;
  const ids = {
    descripcion: useId(),
    proveedor: useId(),
    partida: useId(),
    cantidad: useId(),
    unidad: useId(),
    peso: useId(),
    fob: useId(),
    fragil: useId(),
  };

  useEffect(() => {
    if (!abierto) return;
    setF(inicial(datosIniciales, colorSugerido));
    setIntentado(false);
    setError(null);
  }, [abierto, datosIniciales, colorSugerido]);

  const v = {
    cantidad: numero(f.cantidad),
    largo: numero(f.largo),
    ancho: numero(f.ancho),
    alto: numero(f.alto),
    peso: numero(f.peso),
    fob: numero(f.fob),
  };
  const volumen =
    v.largo > 0 && v.ancho > 0 && v.alto > 0 ? volumenDe(v.largo, v.ancho, v.alto) : 0;

  const errores = useMemo(() => {
    const e: Partial<Record<keyof Formulario, string>> = {};
    if (f.descripcion.trim().length < 2) e.descripcion = 'Escribe qué producto es.';
    if (!f.proveedor.trim()) e.proveedor = 'Elige o agrega el proveedor.';
    if (!f.partida) e.partida = 'Elige la partida arancelaria.';
    if (!(v.cantidad > 0) || !Number.isInteger(v.cantidad))
      e.cantidad = 'Una cantidad entera, mayor que cero.';
    if (!(v.largo > 0) || !(v.ancho > 0) || !(v.alto > 0))
      e.largo = 'Las tres medidas, en centímetros.';
    else if (v.largo > 2000 || v.ancho > 400 || v.alto > 400)
      e.largo = 'Un bulto así no entra en ningún contenedor: revisa las medidas.';
    if (!(v.peso > 0)) e.peso = 'El peso de una unidad, en kilos.';
    if (!(v.fob > 0)) e.fob = 'El precio en origen de una unidad.';
    return e;
  }, [f, v.cantidad, v.largo, v.ancho, v.alto, v.peso, v.fob]);
  const hayErrores = Object.keys(errores).length > 0;
  const mostrar = (k: keyof Formulario) => (intentado ? errores[k] : undefined);

  const guardar = async () => {
    setIntentado(true);
    if (hayErrores) return;
    setGuardando(true);
    setError(null);
    try {
      await onGuardar(
        {
          descripcion: f.descripcion.trim(),
          proveedor: f.proveedor.trim(),
          partidaId: f.partida?.id ?? null,
          cantidad: v.cantidad,
          unidad: f.unidad,
          largoCm: v.largo,
          anchoCm: v.ancho,
          altoCm: v.alto,
          pesoKg: v.peso,
          fobUnitario: v.fob,
          color: f.color,
          fragil: f.fragil,
          materialId: f.materialId,
        },
        datosIniciales?.producto?.id
      );
    } catch (e) {
      setError(mensajeDeError(e, 'No se pudo guardar el producto. Vuelve a intentar.'));
    } finally {
      setGuardando(false);
    }
  };

  const delCatalogo = (m: ProductoDelCatalogo) =>
    setF((x) => ({
      ...x,
      descripcion: m.nombre,
      materialId: m.id,
      proveedor: m.proveedor ?? x.proveedor,
      partida: m.partida ?? x.partida,
      unidad: m.unidad ?? x.unidad,
      largo: m.largoCm ? texto(m.largoCm) : x.largo,
      ancho: m.anchoCm ? texto(m.anchoCm) : x.ancho,
      alto: m.altoCm ? texto(m.altoCm) : x.alto,
      peso: m.pesoKg ? texto(m.pesoKg) : x.peso,
      fob: m.fobUnitario ? texto(m.fobUnitario) : x.fob,
      color: m.color ?? x.color,
      fragil: m.fragil,
    }));

  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && !guardando && onCerrar()}>
      {/*
        Las medidas de la maqueta, pasadas a pantalla: la captura está al 125 %
        (la escala de Windows), así que sus 900 px de ancho son 720 y el título,
        20 px. El botón de cerrar va en círculo con borde, como allá.
      */}
      <DialogContent className="max-w-[720px] gap-0 p-0 sm:rounded-[22px] [&>button:last-child]:right-5 [&>button:last-child]:top-5 [&>button:last-child]:grid [&>button:last-child]:h-9 [&>button:last-child]:w-9 [&>button:last-child]:place-items-center [&>button:last-child]:rounded-full [&>button:last-child]:border [&>button:last-child]:bg-card [&>button:last-child]:opacity-100">
        <DialogHeader className="px-7 pb-2 pt-6">
          <DialogTitle className="font-titulo text-[20px] font-semibold">
            {editando ? 'Editar producto' : 'Agregar producto'}
          </DialogTitle>
          {/* La maqueta no lleva bajada; queda para los lectores de pantalla. */}
          <DialogDescription className="sr-only">
            Lo que quieres traer, tal como te lo vende el proveedor. Todo es obligatorio: sin
            medidas no se puede acomodar ni repartir el flete.
          </DialogDescription>
        </DialogHeader>

        {/*
          `min-w-0`: el formulario es hijo de una grilla (DialogContent) y, sin
          esto, el texto largo de la partida elegida estiraba todo el diálogo a
          lo ancho, con una barra de desplazamiento horizontal.
        */}
        <form
          noValidate
          className="min-w-0 space-y-5 px-7 pb-2 pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            void guardar();
          }}
        >
          <Campo etiqueta="Descripción" htmlFor={ids.descripcion} error={mostrar('descripcion')}>
            <DescripcionConCatalogo
              id={ids.descripcion}
              valor={f.descripcion}
              materialId={f.materialId}
              invalido={!!mostrar('descripcion')}
              onCambio={(descripcion) => setF((x) => ({ ...x, descripcion, materialId: null }))}
              onElegir={delCatalogo}
            />
          </Campo>

          <Campo etiqueta="Proveedor" htmlFor={ids.proveedor} error={mostrar('proveedor')}>
            <SelectorDeProveedor
              id={ids.proveedor}
              valor={f.proveedor}
              invalido={!!mostrar('proveedor')}
              onCambio={(proveedor) => setF((x) => ({ ...x, proveedor }))}
            />
          </Campo>

          <Campo etiqueta="Partida arancelaria" htmlFor={ids.partida} error={mostrar('partida')}>
            <BuscadorDePartida
              id={ids.partida}
              valor={f.partida}
              invalido={!!mostrar('partida')}
              onCambio={(partida) => setF((x) => ({ ...x, partida }))}
            />
          </Campo>

          <div className="grid gap-5 sm:grid-cols-2">
            <Campo etiqueta="Cantidad" htmlFor={ids.cantidad} error={mostrar('cantidad')}>
              <CampoNumero
                id={ids.cantidad}
                valor={f.cantidad}
                invalido={!!mostrar('cantidad')}
                onCambio={(cantidad) => setF((x) => ({ ...x, cantidad }))}
                placeholder="0"
                entero
              />
            </Campo>
            <Campo etiqueta="Unidad" htmlFor={ids.unidad}>
              <select
                id={ids.unidad}
                value={f.unidad}
                onChange={(e) =>
                  setF((x) => ({ ...x, unidad: e.target.value as UnidadDeProducto }))
                }
                className="h-12 w-full rounded-xl border border-input bg-campo px-4 text-[15px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {UNIDADES.map((u) => (
                  <option key={u.valor} value={u.valor}>
                    {u.sigla} · {u.nombre}
                  </option>
                ))}
              </select>
            </Campo>
          </div>

          <fieldset>
            <legend className="mb-2 text-sm font-semibold">
              Dimensiones por unidad <span className="text-destructive">*</span>
            </legend>
            <div className="grid gap-4 sm:grid-cols-[1fr_250px]">
              <div className="space-y-2.5">
                {(
                  [
                    ['largo', 'Largo'],
                    ['ancho', 'Ancho'],
                    ['alto', 'Alto'],
                  ] as const
                ).map(([clave, nombre]) => (
                  <label
                    key={clave}
                    className={cn(
                      'flex h-12 items-center gap-3 rounded-xl border bg-campo px-4 focus-within:ring-2 focus-within:ring-ring',
                      mostrar('largo') ? 'border-destructive' : 'border-input'
                    )}
                  >
                    <span className="flex-1 text-sm text-muted-foreground">{nombre}</span>
                    <input
                      inputMode="decimal"
                      value={f[clave]}
                      onChange={(e) => setF((x) => ({ ...x, [clave]: e.target.value }))}
                      aria-label={`${nombre} en centímetros`}
                      placeholder="0"
                      className="w-24 bg-transparent text-right text-[15px] font-semibold tabular-nums outline-none"
                    />
                    <span className="text-sm text-muted-foreground">cm</span>
                  </label>
                ))}
                <p className="text-sm text-muted-foreground">
                  Volumen por unidad:{' '}
                  <strong className="font-semibold text-foreground tabular-nums">
                    {m3(volumen, 3)}
                  </strong>
                </p>
                {mostrar('largo') && <p className="text-xs text-destructive">{mostrar('largo')}</p>}
              </div>
              <CajaAEscala largo={v.largo} ancho={v.ancho} alto={v.alto} color={f.color} />
            </div>
          </fieldset>

          <div className="grid gap-5 sm:grid-cols-2">
            <Campo etiqueta="Peso por unidad" htmlFor={ids.peso} error={mostrar('peso')}>
              <CampoNumero
                id={ids.peso}
                valor={f.peso}
                invalido={!!mostrar('peso')}
                onCambio={(peso) => setF((x) => ({ ...x, peso }))}
                placeholder="0,0"
                sufijo="kg"
              />
            </Campo>
            <Campo etiqueta="Precio FOB unitario" htmlFor={ids.fob} error={mostrar('fob')}>
              <CampoNumero
                id={ids.fob}
                valor={f.fob}
                invalido={!!mostrar('fob')}
                onCambio={(fob) => setF((x) => ({ ...x, fob }))}
                placeholder="0,00"
                sufijo="USD"
              />
            </Campo>
          </div>

          <fieldset>
            <legend className="mb-2 text-sm font-semibold">
              Color en el plano de carga <span className="text-destructive">*</span>
            </legend>
            <div
              className="flex flex-wrap gap-2.5"
              role="radiogroup"
              aria-label="Color en el plano de carga"
            >
              {COLORES_DE_CARGA.map((c) => {
                const elegido = f.color.toLowerCase() === c.toLowerCase();
                return (
                  <button
                    key={c}
                    type="button"
                    role="radio"
                    aria-checked={elegido}
                    aria-label={NOMBRE_DE_COLOR[c] ?? c}
                    onClick={() => setF((x) => ({ ...x, color: c }))}
                    className={cn(
                      'grid h-8 w-8 place-items-center rounded-full ring-offset-2 ring-offset-background transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      elegido && 'ring-2 ring-foreground'
                    )}
                    style={{ backgroundColor: c }}
                  >
                    {elegido && <Check className="h-4 w-4 text-white" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="flex items-center justify-between gap-4 rounded-xl border bg-surface-sunken px-4 py-3">
            <label htmlFor={ids.fragil} className="text-sm">
              <span className="font-semibold">Frágil</span>
              <span className="block text-xs text-muted-foreground">
                Al acomodar, encima sólo va el mismo producto, en pilas bajas.
              </span>
            </label>
            <Switch
              id={ids.fragil}
              checked={f.fragil}
              onCheckedChange={(fragil) => setF((x) => ({ ...x, fragil }))}
            />
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-bloqueo-suave px-3 py-2 text-sm text-bloqueo">
              {error}
            </p>
          )}

          {/* El pie queda pegado abajo: el formulario es largo y el botón tiene que verse. */}
          <div className="sticky bottom-0 -mx-7 flex flex-wrap items-center justify-between gap-3 border-t bg-surface-2 px-7 py-4">
            <p className="text-sm text-muted-foreground tabular-nums">
              {v.cantidad > 0
                ? `${formatoCantidad(v.cantidad)} ${siglaDeUnidad(f.unidad)}`
                : `0 ${siglaDeUnidad(f.unidad)}`}
              {v.fob > 0 && v.cantidad > 0 && <> · {usd(v.fob * v.cantidad)} FOB</>}
              {volumen > 0 && v.cantidad > 0 && <> · {m3(volumen * v.cantidad)}</>}
              {v.peso > 0 && v.cantidad > 0 && <> · {kg(v.peso * v.cantidad, true)}</>}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onCerrar} disabled={guardando}>
                Cancelar
              </Button>
              <Button type="submit" variant="degradado" className="px-6" disabled={guardando}>
                {guardando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {editando ? 'Guardar cambios' : 'Agregar producto'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  etiqueta,
  htmlFor,
  error,
  children,
}: {
  etiqueta: string;
  htmlFor: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-2 block text-sm font-semibold">
        {etiqueta} <span className="text-destructive">*</span>
      </label>
      {children}
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}

function CampoNumero({
  id,
  valor,
  onCambio,
  invalido,
  placeholder,
  sufijo,
  entero,
}: {
  id: string;
  valor: string;
  onCambio: (v: string) => void;
  invalido?: boolean;
  placeholder?: string;
  sufijo?: string;
  entero?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex h-12 items-center gap-2 rounded-xl border bg-campo px-4 focus-within:ring-2 focus-within:ring-ring',
        invalido ? 'border-destructive' : 'border-input'
      )}
    >
      <input
        id={id}
        inputMode={entero ? 'numeric' : 'decimal'}
        value={valor}
        onChange={(e) => onCambio(e.target.value)}
        placeholder={placeholder}
        aria-invalid={invalido || undefined}
        className="min-w-0 flex-1 bg-transparent text-[15px] tabular-nums outline-none placeholder:text-muted-foreground"
      />
      {sufijo && <span className="text-sm text-muted-foreground">{sufijo}</span>}
    </div>
  );
}

/**
 * La descripción, con los productos que ya están en Datos Maestros: si ya existe,
 * se elige y el formulario se llena con sus medidas, su partida y lo de la última
 * vez que se importó (Melanie: «si ya existe, que lo prellene»).
 */
function DescripcionConCatalogo({
  id,
  valor,
  materialId,
  invalido,
  onCambio,
  onElegir,
}: {
  id: string;
  valor: string;
  materialId: string | null;
  invalido: boolean;
  onCambio: (v: string) => void;
  onElegir: (m: ProductoDelCatalogo) => void;
}) {
  const [opciones, setOpciones] = useState<ProductoDelCatalogo[]>([]);
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const listaId = useId();

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  useEffect(() => {
    if (materialId || valor.trim().length < 2) {
      setOpciones([]);
      return;
    }
    let vigente = true;
    const t = setTimeout(() => {
      comerciaService
        .catalogo(valor.trim())
        .then((r) => vigente && setOpciones(r))
        .catch(() => vigente && setOpciones([]));
    }, 250);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [valor, materialId]);

  const mostrar = abierto && opciones.length > 0 && !materialId;
  return (
    <div ref={caja} className="relative">
      <input
        id={id}
        value={valor}
        role="combobox"
        aria-expanded={mostrar}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-invalid={invalido || undefined}
        onChange={(e) => {
          onCambio(e.target.value);
          setAbierto(true);
        }}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => e.key === 'Escape' && setAbierto(false)}
        placeholder="Ej: Caja con notebooks Lenovo"
        className={cn(
          'h-12 w-full rounded-xl border bg-campo px-4 text-[15px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          invalido ? 'border-destructive' : 'border-input'
        )}
      />
      {materialId && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-ok">
          <PackageCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Ya está en tu catálogo (Datos Maestros): se completó con lo último que cargaste.
        </p>
      )}
      {mostrar && (
        <div
          id={listaId}
          role="listbox"
          aria-label="Productos de tu catálogo"
          className="absolute z-50 mt-1.5 w-full overflow-hidden rounded-xl border bg-popover p-1 shadow-overlay"
        >
          <p className="px-3 py-1.5 text-xs font-semibold text-muted-foreground">
            Ya en tu catálogo
          </p>
          {opciones.map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => {
                onElegir(m);
                setAbierto(false);
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-accent"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{m.nombre}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {m.codigo}
                  {m.largoCm && m.anchoCm && m.altoCm
                    ? ` · ${formatoFijo(m.largoCm, 0)} × ${formatoFijo(m.anchoCm, 0)} × ${formatoFijo(m.altoCm, 0)} cm`
                    : ''}
                  {m.proveedor ? ` · ${m.proveedor}` : ''}
                </span>
              </span>
              {m.costoNacionalizado != null && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  nacionalizado {usd(m.costoNacionalizado)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** «Busca o agrega el proveedor»: los ya usados, o uno nuevo con lo escrito. */
function SelectorDeProveedor({
  id,
  valor,
  invalido,
  onCambio,
}: {
  id: string;
  valor: string;
  invalido: boolean;
  onCambio: (v: string) => void;
}) {
  const [opciones, setOpciones] = useState<string[]>([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const listaId = useId();

  useEffect(() => {
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, []);

  useEffect(() => {
    if (!abierto) return;
    let vigente = true;
    setCargando(true);
    const t = setTimeout(() => {
      comerciaService
        .proveedores(valor.trim())
        .then((r) => vigente && setOpciones(r))
        .catch(() => vigente && setOpciones([]))
        .finally(() => vigente && setCargando(false));
    }, 200);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [valor, abierto]);

  const escrito = valor.trim();
  const existe = opciones.some((o) => o.toLowerCase() === escrito.toLowerCase());
  // Mientras busca no se abre una caja vacía: o hay proveedores, o se ofrece el nuevo.
  const mostrar = abierto && (opciones.length > 0 || (escrito.length > 0 && !existe && !cargando));

  return (
    <div ref={caja} className="relative">
      <Search
        className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        id={id}
        value={valor}
        role="combobox"
        aria-expanded={mostrar}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-invalid={invalido || undefined}
        onChange={(e) => {
          onCambio(e.target.value);
          setAbierto(true);
        }}
        onFocus={() => setAbierto(true)}
        onKeyDown={(e) => e.key === 'Escape' && setAbierto(false)}
        placeholder="Busca o agrega el proveedor"
        className={cn(
          'h-12 w-full rounded-xl border bg-campo pl-11 pr-4 text-[15px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          invalido ? 'border-destructive' : 'border-input'
        )}
      />
      {mostrar && (
        <div
          id={listaId}
          role="listbox"
          aria-label="Proveedores"
          className="absolute z-50 mt-1.5 max-h-64 w-full overflow-y-auto rounded-xl border bg-popover p-1 shadow-overlay"
        >
          {opciones.map((o) => (
            <button
              key={o}
              type="button"
              role="option"
              aria-selected={o === valor}
              onClick={() => {
                onCambio(o);
                setAbierto(false);
              }}
              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm hover:bg-accent"
            >
              {o}
            </button>
          ))}
          {escrito && !existe && !cargando && (
            <button
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => setAbierto(false)}
              className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-primary hover:bg-accent"
            >
              Agregar «{escrito}» como proveedor nuevo
            </button>
          )}
        </div>
      )}
    </div>
  );
}
